import React, { useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Banknote,
  Search,
  RefreshCw,
  Plus,
  X,
  DollarSign,
  Building2,
  ExternalLink,
  ChevronRight,
  Wallet,
  ArrowDownLeft,
  CheckCircle2,
  FileText,
  AlertCircle,
  ArrowLeft,
  Filter,
  History,
  Trash2,
} from 'lucide-react';
import { useToastContext } from '../../contexts/ToastContext';
import {
  getAdminVendorPayoutVendors,
  getAdminVendorDisbursements,
  postAdminVendorDisbursement,
  deleteAdminVendorDisbursement,
} from '../../services/vendorPayoutService';
import { formatDateTime } from '../../utils/dateUtils';

const fmtMoney = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '$0.00';
  return `$${v.toFixed(2)}`;
};

const AdminVendorPayouts = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters & Tabs
  const [activeFilterTab, setActiveFilterTab] = useState('due_only'); // 'due_only' | 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('business_name:asc');
  const [vendorPage, setVendorPage] = useState(1);

  // Selected Vendor & History Pagination
  const [selectedVendorId, setSelectedVendorId] = useState(searchParams.get('vendor_id') || null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historySearch, setHistorySearch] = useState('');

  // Mobile View state (show details on mobile when vendor selected)
  const [mobileShowDetails, setMobileShowDetails] = useState(false);

  // Modal State for Recording Payout
  const [payVendor, setPayVendor] = useState(null);
  const [payForm, setPayForm] = useState({
    amount: '',
    notes: '',
    booking_id: '',
    currency: 'USD',
  });

  // State for Delete Confirmation Modal
  const [deleteDisbursementTarget, setDeleteDisbursementTarget] = useState(null);

  // Query parameters for Vendor List
  const queryParams = useMemo(() => {
    const [sort_by, sort_order] = sortBy.split(':');
    return {
      page: vendorPage,
      per_page: 30,
      sort_by: sort_by || 'business_name',
      sort_order: sort_order || 'asc',
      due_only: activeFilterTab === 'due_only',
      ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
    };
  }, [vendorPage, sortBy, activeFilterTab, searchQuery]);

  // Fetch Vendors
  const {
    data: vendorsData,
    isLoading: vendorsLoading,
    error: vendorsError,
    refetch: refetchVendors,
    isFetching: vendorsFetching,
  } = useQuery({
    queryKey: ['admin-vendor-payout-vendors', queryParams],
    queryFn: async () => getAdminVendorPayoutVendors(queryParams),
    placeholderData: (prev) => prev,
  });

  const pagination = vendorsData?.data;
  const vendorList = useMemo(() => pagination?.data ?? [], [pagination?.data]);

  // Automatically select the first vendor if none selected or if selected vendor isn't in view
  const selectedVendor = useMemo(() => {
    if (!vendorList.length) return null;
    if (selectedVendorId) {
      const found = vendorList.find((v) => String(v.id) === String(selectedVendorId));
      if (found) return found;
    }
    return vendorList[0];
  }, [vendorList, selectedVendorId]);

  // Keep search params in sync with selection
  useEffect(() => {
    if (selectedVendor && String(selectedVendor.id) !== selectedVendorId) {
      setSelectedVendorId(selectedVendor.id);
    }
  }, [selectedVendor, selectedVendorId]);

  // Fetch Disbursements for Selected Vendor
  const selectedVendorProfileId = selectedVendor?.id;
  const {
    data: disbursementsData,
    isLoading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
    isFetching: historyFetching,
  } = useQuery({
    queryKey: ['admin-vendor-disbursements', selectedVendorProfileId, historyPage],
    queryFn: async () =>
      getAdminVendorDisbursements(selectedVendorProfileId, { page: historyPage, per_page: 15 }),
    enabled: Boolean(selectedVendorProfileId),
    placeholderData: (prev) => prev,
  });

  const historyPagination = disbursementsData?.data;
  const allDisbursements = useMemo(
    () => historyPagination?.data ?? [],
    [historyPagination?.data]
  );

  // Client-side filter for history search if needed
  const filteredDisbursements = useMemo(() => {
    if (!historySearch.trim()) return allDisbursements;
    const term = historySearch.toLowerCase();
    return allDisbursements.filter(
      (d) =>
        d.disbursement_reference?.toLowerCase().includes(term) ||
        d.notes?.toLowerCase().includes(term) ||
        d.booking?.booking_reference?.toLowerCase().includes(term) ||
        d.recorded_by?.name?.toLowerCase().includes(term) ||
        d.recorded_by?.email?.toLowerCase().includes(term)
    );
  }, [allDisbursements, historySearch]);

  // Top KPI calculations from loaded vendors
  const kpis = useMemo(() => {
    let totalDue = 0;
    let totalDisbursed = 0;
    let vendorsWithDueCount = 0;

    vendorList.forEach((v) => {
      const avail = Number(v.ledger?.available_balance || 0);
      const disbursed = Number(v.ledger?.total_disbursed || 0);
      if (avail > 0) vendorsWithDueCount += 1;
      totalDue += avail;
      totalDisbursed += disbursed;
    });

    return {
      totalDue,
      totalDisbursed,
      vendorsWithDueCount,
      totalVendors: pagination?.total ?? vendorList.length,
    };
  }, [vendorList, pagination]);

  // Mutation to Record Payout
  const recordMutation = useMutation({
    mutationFn: async () => {
      const amount = parseFloat(String(payForm.amount).replace(/,/g, ''), 10);
      if (Number.isNaN(amount) || amount <= 0) {
        throw new Error('Enter a valid amount greater than zero.');
      }
      const avail = Number(payVendor?.ledger?.available_balance || 0);
      if (avail <= 0) {
        throw new Error('This vendor has $0.00 available balance due to pay.');
      }
      if (amount > avail + 0.001) {
        throw new Error(
          `Amount cannot exceed the available balance due (${fmtMoney(avail)}).`
        );
      }
      const payload = {
        amount,
        currency: (payForm.currency || 'USD').trim().toUpperCase() || 'USD',
        notes: payForm.notes?.trim() || undefined,
        booking_id: payForm.booking_id?.trim() || undefined,
      };
      return postAdminVendorDisbursement(payVendor.id, payload);
    },
    onSuccess: (res) => {
      success(res?.message || 'Payout disbursement recorded successfully.');
      queryClient.invalidateQueries({ queryKey: ['admin-vendor-payout-vendors'] });
      if (selectedVendorProfileId === payVendor?.id) {
        queryClient.invalidateQueries({
          queryKey: ['admin-vendor-disbursements', payVendor.id],
        });
      }
      setPayVendor(null);
      setPayForm({ amount: '', notes: '', booking_id: '', currency: 'USD' });
    },
    onError: (err) => {
      showError(err?.message || 'Could not record disbursement.');
    },
  });

  // Mutation to Delete Disbursement
  const deleteMutation = useMutation({
    mutationFn: async (disbursementId) => {
      if (!selectedVendorProfileId) throw new Error('No vendor selected.');
      return deleteAdminVendorDisbursement(selectedVendorProfileId, disbursementId);
    },
    onSuccess: (res) => {
      success(res?.message || 'Disbursement record deleted.');
      queryClient.invalidateQueries({ queryKey: ['admin-vendor-payout-vendors'] });
      if (selectedVendorProfileId) {
        queryClient.invalidateQueries({
          queryKey: ['admin-vendor-disbursements', selectedVendorProfileId],
        });
      }
      setDeleteDisbursementTarget(null);
    },
    onError: (err) => {
      showError(err?.message || 'Could not delete disbursement.');
    },
  });

  const handleOpenPayModal = (vendor) => {
    const target = vendor || selectedVendor;
    if (!target) return;
    const avail = target.ledger?.available_balance ?? 0;
    setPayVendor(target);
    setPayForm({
      amount: avail > 0 ? String(avail.toFixed(2)) : '',
      notes: '',
      booking_id: '',
      currency: 'USD',
    });
  };

  const handleSelectVendor = (v) => {
    setSelectedVendorId(v.id);
    setSearchParams({ vendor_id: v.id });
    setHistoryPage(1);
    setHistorySearch('');
    setMobileShowDetails(true);
  };

  const parsedPayAmount = parseFloat(String(payForm.amount).replace(/,/g, ''));
  const payAvail = Number(payVendor?.ledger?.available_balance || 0);
  const isPayAmountOverdue =
    !Number.isNaN(parsedPayAmount) && parsedPayAmount > payAvail + 0.001;
  const isPayDisabled =
    recordMutation.isPending ||
    payAvail <= 0 ||
    Number.isNaN(parsedPayAmount) ||
    parsedPayAmount <= 0 ||
    isPayAmountOverdue;

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto pb-12">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2.5">
            <div className="p-2 bg-black text-white rounded-xl shadow-sm">
              <Banknote className="w-6 h-6" />
            </div>
            Vendor Payouts & Disbursements
          </h1>
          <p className="text-gray-600 mt-1 text-sm max-w-2xl">
            Track order earnings, calculate platform commission balances, and record manual external payouts sent to vendors.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              refetchVendors();
              if (selectedVendorProfileId) refetchHistory();
            }}
            disabled={vendorsFetching || historyFetching}
            className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-50 transition-colors"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 text-gray-500 ${
                vendorsFetching || historyFetching ? 'animate-spin' : ''
              }`}
            />
            Refresh Data
          </button>
          {selectedVendor && (
            <button
              type="button"
              onClick={() => handleOpenPayModal(selectedVendor)}
              disabled={(selectedVendor.ledger?.available_balance ?? 0) <= 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black text-white text-sm font-medium hover:bg-gray-800 shadow-sm transition-colors disabled:opacity-40 disabled:hover:bg-black"
              title={
                (selectedVendor.ledger?.available_balance ?? 0) <= 0
                  ? 'All earnings for this vendor have been settled'
                  : 'Record payout disbursement'
              }
            >
              <Plus className="w-4 h-4" />
              Record Payout
            </button>
          )}
        </div>
      </div>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
              Total Balance Due
            </span>
            <div className="p-2 rounded-xl bg-amber-50 text-amber-700">
              <Wallet className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{fmtMoney(kpis.totalDue)}</p>
          <p className="text-xs text-gray-500 mt-1">Pending disbursement to vendors</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">
              Total Disbursed (Sent)
            </span>
            <div className="p-2 rounded-xl bg-blue-50 text-blue-700">
              <ArrowDownLeft className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{fmtMoney(kpis.totalDisbursed)}</p>
          <p className="text-xs text-gray-500 mt-1">Total recorded payouts sent to date</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
              Vendors Due Payout
            </span>
            <div className="p-2 rounded-xl bg-emerald-50 text-emerald-700">
              <Building2 className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{kpis.vendorsWithDueCount}</p>
          <p className="text-xs text-gray-500 mt-1">Vendors with balance &gt; $0.00</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
              Selected Vendor Due
            </span>
            <div className="p-2 rounded-xl bg-gray-100 text-gray-700">
              <DollarSign className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            {selectedVendor ? fmtMoney(selectedVendor.ledger?.available_balance) : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-1 truncate">
            {selectedVendor ? selectedVendor.business_name : 'No vendor selected'}
          </p>
        </div>
      </div>

      {/* Main Two-Pane Workspace Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* LEFT COLUMN: Vendor Directory List */}
        <div
          className={`lg:col-span-4 xl:col-span-3 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col ${
            mobileShowDetails ? 'hidden lg:flex' : 'flex'
          }`}
        >
          {/* List Controls & Search */}
          <div className="p-3.5 border-b border-gray-100 space-y-2.5 bg-gray-50/50">
            {/* Filter Tabs */}
            <div className="flex p-1 bg-gray-200/70 rounded-xl">
              <button
                type="button"
                onClick={() => {
                  setActiveFilterTab('due_only');
                  setVendorPage(1);
                }}
                className={`flex-1 py-1 text-xs font-semibold rounded-lg transition-all ${
                  activeFilterTab === 'due_only'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Due to Pay
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveFilterTab('all');
                  setVendorPage(1);
                }}
                className={`flex-1 py-1 text-xs font-semibold rounded-lg transition-all ${
                  activeFilterTab === 'all'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All Vendors
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setVendorPage(1);
                }}
                placeholder="Search vendor…"
                className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all placeholder:text-gray-400"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center justify-between text-xs text-gray-500 px-0.5">
              <span>{vendorList.length} vendors</span>
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-gray-400" />
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setVendorPage(1);
                  }}
                  className="bg-transparent border-0 text-xs font-medium text-gray-700 focus:ring-0 cursor-pointer p-0"
                >
                  <option value="business_name:asc">Name (A–Z)</option>
                  <option value="business_name:desc">Name (Z–A)</option>
                  <option value="created_at:desc">Newest</option>
                  <option value="created_at:asc">Oldest</option>
                </select>
              </div>
            </div>
          </div>

          {/* Vendors Scrollable List */}
          <div className="divide-y divide-gray-100 max-h-[calc(100vh-280px)] overflow-y-auto">
            {vendorsLoading && !vendorList.length ? (
              <div className="py-12 text-center space-y-2">
                <div className="w-7 h-7 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto" />
                <p className="text-xs text-gray-500">Loading vendors…</p>
              </div>
            ) : vendorsError ? (
              <div className="p-4 text-center text-xs text-red-600 space-y-2">
                <AlertCircle className="w-5 h-5 mx-auto text-red-500" />
                <p>{vendorsError.message || 'Failed to load vendors.'}</p>
              </div>
            ) : vendorList.length === 0 ? (
              <div className="p-6 text-center text-gray-500 space-y-2">
                <Building2 className="w-7 h-7 mx-auto text-gray-300" />
                <p className="text-xs font-medium text-gray-700">No vendors found</p>
                <p className="text-[11px] text-gray-400">
                  {activeFilterTab === 'due_only'
                    ? 'No vendors currently have an outstanding payout balance.'
                    : 'No vendors match your search.'}
                </p>
              </div>
            ) : (
              vendorList.map((vendor) => {
                const isSelected = selectedVendor?.id === vendor.id;
                const avail = Number(vendor.ledger?.available_balance || 0);
                const disbursed = Number(vendor.ledger?.total_disbursed || 0);

                return (
                  <button
                    key={vendor.id}
                    type="button"
                    onClick={() => handleSelectVendor(vendor)}
                    className={`w-full text-left p-3.5 transition-all flex items-start gap-2.5 relative ${
                      isSelected
                        ? 'bg-black/[0.03] border-l-4 border-black font-medium'
                        : 'hover:bg-gray-50 border-l-4 border-transparent'
                    }`}
                  >
                    {/* Avatar / Initials */}
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 shadow-sm ${
                        isSelected
                          ? 'bg-black text-white'
                          : 'bg-gray-100 text-gray-700 border border-gray-200'
                      }`}
                    >
                      {(vendor.business_name || 'V').charAt(0).toUpperCase()}
                    </div>

                    {/* Vendor Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1.5">
                        <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                          {vendor.business_name || 'Unnamed Vendor'}
                        </p>
                        {avail > 0 ? (
                          <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900">
                            {fmtMoney(avail)}
                          </span>
                        ) : (
                          <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                            Settled
                          </span>
                        )}
                      </div>

                      <p className="text-[11px] text-gray-500 truncate mt-0.5">
                        {vendor.user?.email || 'No email attached'}
                      </p>

                      <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-100/80 text-[11px] text-gray-500">
                        <span>
                          Sent: <strong className="text-gray-700">{fmtMoney(disbursed)}</strong>
                        </span>
                        <span className="flex items-center text-[11px] font-medium text-black">
                          View
                          <ChevronRight className="w-3 h-3 ml-0.5 text-gray-400" />
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* List Pagination */}
          {pagination && pagination.last_page > 1 && (
            <div className="p-2.5 border-t border-gray-100 flex items-center justify-between text-xs bg-gray-50/50">
              <span className="text-[11px] text-gray-500">
                {pagination.current_page} / {pagination.last_page}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  disabled={pagination.current_page <= 1}
                  onClick={() => setVendorPage((p) => Math.max(1, p - 1))}
                  className="px-2 py-0.5 rounded border border-gray-200 bg-white text-gray-700 text-xs disabled:opacity-40 hover:bg-gray-50"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={pagination.current_page >= pagination.last_page}
                  onClick={() => setVendorPage((p) => p + 1)}
                  className="px-2 py-0.5 rounded border border-gray-200 bg-white text-gray-700 text-xs disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Vendor Payout Details & Payment History */}
        <div
          className={`lg:col-span-8 xl:col-span-9 space-y-6 ${
            !mobileShowDetails ? 'hidden lg:block' : 'block'
          }`}
        >
          {/* Mobile Back Button */}
          <div className="lg:hidden mb-2">
            <button
              type="button"
              onClick={() => setMobileShowDetails(false)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-black py-1 px-2.5 rounded-lg bg-gray-100"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to vendors list
            </button>
          </div>

          {selectedVendor ? (
            <div className="space-y-6">
              {/* Selected Vendor Profile Banner */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-black text-white flex items-center justify-center font-bold text-xl shadow-md">
                      {(selectedVendor.business_name || 'V').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-gray-900">
                          {selectedVendor.business_name || 'Unnamed Vendor'}
                        </h2>
                        {selectedVendor.user_id && (
                          <Link
                            to={`/admin/vendors/${selectedVendor.user_id || selectedVendor.user?.id}`}
                            className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 px-2 py-0.5 rounded-md transition-colors"
                            title="View vendor management profile"
                          >
                            <span>Profile</span>
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {selectedVendor.user?.email || 'No email associated'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => handleOpenPayModal(selectedVendor)}
                      disabled={(selectedVendor.ledger?.available_balance ?? 0) <= 0}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-black text-white text-sm font-semibold hover:bg-gray-800 shadow-sm transition-all disabled:opacity-40 disabled:hover:bg-black"
                    >
                      <Plus className="w-4 h-4" />
                      Record Payout
                    </button>
                  </div>
                </div>

                {/* Ledger Financial Summary Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6">
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-200/80">
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <span>Earned (Orders)</span>
                      <DollarSign className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mt-1.5">
                      {fmtMoney(selectedVendor.ledger?.total_earned_from_orders)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Vendor share from completed bookings</p>
                  </div>

                  <div className="p-4 rounded-xl bg-blue-50/70 border border-blue-200/80">
                    <div className="flex items-center justify-between text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      <span>Total Sent (Disbursed)</span>
                      <ArrowDownLeft className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-blue-900 mt-1.5">
                      {fmtMoney(selectedVendor.ledger?.total_disbursed)}
                    </p>
                    <p className="text-xs text-blue-700/80 mt-1">
                      Total manual payouts recorded
                    </p>
                  </div>

                  <div
                    className={`p-4 rounded-xl border ${
                      (selectedVendor.ledger?.available_balance ?? 0) > 0
                        ? 'bg-amber-50/70 border-amber-200'
                        : 'bg-emerald-50/70 border-emerald-200'
                    }`}
                  >
                    <div
                      className={`flex items-center justify-between text-xs font-semibold uppercase tracking-wider ${
                        (selectedVendor.ledger?.available_balance ?? 0) > 0
                          ? 'text-amber-700'
                          : 'text-emerald-700'
                      }`}
                    >
                      <span>
                        {(selectedVendor.ledger?.available_balance ?? 0) > 0
                          ? 'Balance Due to Pay'
                          : 'Settlement Status'}
                      </span>
                      {(selectedVendor.ledger?.available_balance ?? 0) > 0 ? (
                        <Wallet className="w-4 h-4 text-amber-600" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      )}
                    </div>
                    <p
                      className={`text-2xl font-bold mt-1.5 ${
                        (selectedVendor.ledger?.available_balance ?? 0) > 0
                          ? 'text-amber-900'
                          : 'text-emerald-900'
                      }`}
                    >
                      {fmtMoney(selectedVendor.ledger?.available_balance)}
                    </p>
                    <p
                      className={`text-xs mt-1 ${
                        (selectedVendor.ledger?.available_balance ?? 0) > 0
                          ? 'text-amber-700'
                          : 'text-emerald-700'
                      }`}
                    >
                      {(selectedVendor.ledger?.available_balance ?? 0) > 0
                        ? 'Available to record and send'
                        : 'All earnings settled to date'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Disbursements / Payment History Card */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Section Header */}
                <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-gray-100 text-gray-700 rounded-xl">
                      <History className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">
                        Disbursement Payment History
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Historical list of payout records issued to {selectedVendor.business_name}
                      </p>
                    </div>
                  </div>

                  {/* Search / Filter for History */}
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="search"
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      placeholder="Filter history by ref, booking…"
                      className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-black"
                    />
                  </div>
                </div>

                {/* History Table */}
                <div className="overflow-x-auto">
                  {historyLoading ? (
                    <div className="py-16 text-center space-y-3">
                      <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto" />
                      <p className="text-xs text-gray-500">Loading disbursement history…</p>
                    </div>
                  ) : historyError ? (
                    <div className="p-8 text-center text-xs text-red-600">
                      {historyError.message || 'Failed to load disbursements.'}
                    </div>
                  ) : filteredDisbursements.length === 0 ? (
                    <div className="py-14 text-center px-4 space-y-3">
                      <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mx-auto">
                        <FileText className="w-6 h-6" />
                      </div>
                      <p className="text-sm font-semibold text-gray-900">No disbursements recorded yet</p>
                      <p className="text-xs text-gray-500 max-w-sm mx-auto">
                        When you pay out earnings to this vendor outside the platform, record it here so the vendor can track their payouts.
                      </p>
                      {(selectedVendor.ledger?.available_balance ?? 0) > 0 && (
                        <button
                          type="button"
                          onClick={() => handleOpenPayModal(selectedVendor)}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-black text-white text-xs font-semibold hover:bg-gray-800 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Record First Payout
                        </button>
                      )}
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-100 text-left text-xs sm:text-sm">
                      <thead className="bg-gray-50/70 text-gray-600 font-semibold text-xs">
                        <tr>
                          <th className="px-5 py-3">Reference #</th>
                          <th className="px-5 py-3">Amount</th>
                          <th className="px-5 py-3">Date & Time</th>
                          <th className="px-5 py-3">Linked Booking</th>
                          <th className="px-5 py-3">Notes</th>
                          <th className="px-5 py-3">Recorded By</th>
                          <th className="px-5 py-3 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filteredDisbursements.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-5 py-3.5 font-mono text-xs font-bold text-gray-900 whitespace-nowrap">
                              {item.disbursement_reference}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md font-bold text-xs bg-emerald-50 text-emerald-800 border border-emerald-100">
                                {fmtMoney(item.amount)}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-gray-600 whitespace-nowrap text-xs">
                              {formatDateTime(item.created_at)}
                            </td>
                            <td className="px-5 py-3.5 whitespace-nowrap text-xs">
                              {item.booking?.booking_reference ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 font-mono text-gray-700">
                                  #{item.booking.booking_reference}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-5 py-3.5 text-gray-600 text-xs max-w-xs truncate">
                              {item.notes || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-5 py-3.5 text-gray-500 text-xs whitespace-nowrap">
                              {item.recorded_by?.name || item.recorded_by?.email || 'Admin'}
                            </td>
                            <td className="px-5 py-3.5 text-right whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => setDeleteDisbursementTarget(item)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-100 transition-colors"
                                title="Delete this payout record"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>Delete</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* History Pagination */}
                {historyPagination && historyPagination.last_page > 1 && (
                  <div className="p-4 border-t border-gray-100 flex items-center justify-between text-xs bg-gray-50/50">
                    <span className="text-gray-600">
                      Showing page {historyPagination.current_page} of {historyPagination.last_page} (
                      {historyPagination.total} total payments)
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={historyPage <= 1}
                        onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 text-xs font-medium disabled:opacity-40 hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        disabled={historyPage >= historyPagination.last_page}
                        onClick={() => setHistoryPage((p) => p + 1)}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 text-xs font-medium disabled:opacity-40 hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm space-y-3">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto" />
              <h3 className="text-base font-bold text-gray-900">Select a vendor from the list</h3>
              <p className="text-xs text-gray-500 max-w-sm mx-auto">
                Choose a vendor on the left to view their order earnings ledger, available due balance, and complete disbursement history.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Record Payout Dialog Modal */}
      {payVendor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-100">
                  <DollarSign className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Record Payout Disbursement</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    For <strong className="text-gray-900">{payVendor.business_name}</strong>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !recordMutation.isPending && setPayVendor(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Reference Balances */}
            <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-200/80 flex items-center justify-between text-xs">
              <div>
                <span className="text-gray-500">Available Balance Due:</span>
                <p className="text-base font-bold text-emerald-700">
                  {fmtMoney(payVendor.ledger?.available_balance)}
                </p>
              </div>
              <div className="text-right">
                <span className="text-gray-500">Already Sent:</span>
                <p className="text-base font-semibold text-gray-800">
                  {fmtMoney(payVendor.ledger?.total_disbursed)}
                </p>
              </div>
            </div>

            {payAvail <= 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span>
                  This vendor has <strong>$0.00</strong> available balance due. All completed order earnings have already been fully disbursed.
                </span>
              </div>
            )}

            {/* Form Fields */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                recordMutation.mutate();
              }}
              className="space-y-4"
            >
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Payout Amount ($) *
                  </label>
                  {payAvail > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setPayForm((f) => ({ ...f, amount: String(payAvail.toFixed(2)) }))
                      }
                      className="text-xs text-primary-600 hover:text-black font-semibold hover:underline"
                    >
                      Fill Max ({fmtMoney(payAvail)})
                    </button>
                  )}
                </div>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    required
                    disabled={payAvail <= 0}
                    value={payForm.amount}
                    onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
                    className={`w-full pl-8 pr-3 py-2.5 border rounded-xl text-sm font-semibold text-gray-900 focus:ring-2 focus:ring-black focus:border-black ${
                      isPayAmountOverdue
                        ? 'border-red-400 bg-red-50/40 text-red-900'
                        : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                </div>
                {isPayAmountOverdue && (
                  <p className="text-xs text-red-600 font-medium mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Amount cannot exceed available balance due ({fmtMoney(payAvail)}).
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Currency
                  </label>
                  <input
                    type="text"
                    maxLength={3}
                    value={payForm.currency}
                    onChange={(e) =>
                      setPayForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm uppercase"
                    placeholder="USD"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Booking Reference / ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={payForm.booking_id}
                    onChange={(e) => setPayForm((f) => ({ ...f, booking_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl font-mono text-xs"
                    placeholder="e.g. BK-2B9FTW or UUID"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Notes / Payment Reference
                </label>
                <textarea
                  value={payForm.notes}
                  onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-black"
                  placeholder="Check #, Wire transfer ref, ACH note, billing period..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2.5 justify-end pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setPayVendor(null)}
                  disabled={recordMutation.isPending}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isPayDisabled}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-black text-white text-sm font-semibold hover:bg-gray-800 transition-all disabled:opacity-40 disabled:hover:bg-black shadow-sm"
                >
                  {recordMutation.isPending && (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  )}
                  {recordMutation.isPending ? 'Recording…' : 'Record & Save Payout'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteDisbursementTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-red-600">
              <div className="p-3 bg-red-100 rounded-xl">
                <Trash2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete Disbursement</h3>
                <p className="text-xs text-gray-500">This action will remove this payout entry.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-200 text-xs space-y-1.5">
              <p>
                <strong>Reference:</strong>{' '}
                <span className="font-mono">{deleteDisbursementTarget.disbursement_reference}</span>
              </p>
              <p>
                <strong>Amount:</strong>{' '}
                <span className="font-bold text-emerald-700">
                  {fmtMoney(deleteDisbursementTarget.amount)}
                </span>
              </p>
              {deleteDisbursementTarget.notes && (
                <p className="truncate">
                  <strong>Notes:</strong> {deleteDisbursementTarget.notes}
                </p>
              )}
            </div>

            <p className="text-xs text-gray-600">
              Deleting this disbursement will update the vendor’s ledger and restore{' '}
              <strong>{fmtMoney(deleteDisbursementTarget.amount)}</strong> to their available balance due.
            </p>

            <div className="flex gap-2.5 justify-end pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setDeleteDisbursementTarget(null)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 rounded-xl border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deleteDisbursementTarget.id)}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                {deleteMutation.isPending && (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                )}
                {deleteMutation.isPending ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVendorPayouts;
